import { GetStaticProps, InferGetStaticPropsType } from 'next';
import type { Thing } from 'schema-dts';
import { getCommonPageProps } from '@/shared/api';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import Typografed from '@/shared/components/shared/Typografed';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';

const PAGE_PATH = '/agreement/';
const PAGE_TITLE = 'Согласие на обработку персональных данных';
const PAGE_DESCRIPTION = 'Согласие на обработку персональных данных при использовании сайта oilgasforum.ru.';
const SITE_URL = 'https://oilgasforum.ru/';

const AgreementPage = ({ h1, meta, breadcrumbs, bodyClass }: InferGetStaticPropsType<typeof getStaticProps>) => {
    return (
        <DefaultLayout bodyClass={bodyClass}>
            <div className="privacy js-header-theme-trigger" data-theme="dark">
                <div className="privacy-wrapper wrapper">
                    <Breadcrumbs className="privacy-breadcrumbs" breadcrumbs={breadcrumbs} />

                    <div className="privacy-content">
                        <h1 className="privacy-content__title h2">
                            <Typografed>{h1}</Typografed>
                        </h1>
                        <div className="wysiwyg privacy-content__text">
                            <p>
                                Настоящим я, подтверждая свое совершеннолетие и полную дееспособность, и являясь
                                пользователем сайта <a href={SITE_URL}>{SITE_URL}</a>, предоставляю Ассоциации
                                «Нефтегазовый кластер» (ОГРН 1217200003140, ИНН 7203516308), расположенному по адресу:
                                625026, Россия, Тюменская обл., Тюмень г., Республики ул., д.142 (далее — Оператор),
                                своё согласие на обработку следующих персональных данных:
                            </p>
                            <ul>
                                <li>Фамилия, имя, отчество;</li>
                                <li>Электронный адрес;</li>
                                <li>Номера телефонов;</li>
                                <li>Фотографии и видеосъемки;</li>
                                <li>Адрес и геолокация;</li>
                                <li>Сведения об образовании, профессии, специальности и квалификации;</li>
                                <li>Сведения о месте работы, занимаемой должности;</li>
                                <li>Идентификаторы в социальных сетях;</li>
                            </ul>
                            <p>
                                Предоставляю Оператору право осуществлять следующие действия (операции) с моими
                                персональными данными:
                            </p>
                            <ul>
                                <li>запись;</li>
                                <li>сбор;</li>
                                <li>систематизация;</li>
                                <li>накопление;</li>
                                <li>хранение;</li>
                                <li>уточнение (обновление, изменение);</li>
                                <li>извлечение;</li>
                                <li>
                                    использование, в том числе с применением технологий искусственного интеллекта для
                                    аналитики и формирования отчётов;
                                </li>
                                <li>передачу ООО «ЦМС» (ОГРН 1077203058668, ИНН 7203205528), являющемся Оператором;</li>
                                <li>
                                    передачу ООО «ВКА Спутник» (ОГРН 1077203049880, ИНН 7203202206), являющемся
                                    Оператором;
                                </li>
                                <li>
                                    передачу ИП Петрулёв Дмитрий Сергеевич (ОГРН 315723200060182, ИНН 723003004441),
                                    являющемся Оператором;
                                </li>
                                <li>
                                    распространение в сети Интернет исключительно на сайте{' '}
                                    <a href={SITE_URL}>{SITE_URL}</a>, в социальных сетях сайта{' '}
                                    <a href={SITE_URL}>{SITE_URL}</a>, а также в печатных и электронных материалах
                                    мероприятий Оператора (программа мероприятия, списки участников, презентационные
                                    материалы);
                                </li>
                                <li>обезличивание;</li>
                                <li>блокирование;</li>
                                <li>удаление;</li>
                                <li>уничтожение.</li>
                            </ul>
                            <p>Оператор обрабатывает персональные данные в указанном ниже объёме и целях:</p>
                            <ul>
                                <li>осуществление Оператором своей деятельности в соответствии с уставом;</li>
                                <li>
                                    организация и проведение деловых мероприятий Оператора (форумов, конференций,
                                    выставок), включая формирование деловой программы, списков участников, спикеров и
                                    модераторов;
                                </li>
                                <li>
                                    включение субъекта персональных данных в программу мероприятия и сопутствующие
                                    материалы в качестве участника, спикера, модератора или эксперта;
                                </li>
                                <li>
                                    публикация сведений об участии субъекта персональных данных в мероприятии на сайте,
                                    в социальных сетях и в материалах мероприятия Оператора;
                                </li>
                                <li>информирование пользователя посредством отправки электронных писем;</li>
                                <li>
                                    предоставление доступа пользователю к сервисам, информации и (или) материалам,
                                    содержащимся на веб-сайте <a href={SITE_URL}>{SITE_URL}</a>.
                                </li>
                            </ul>
                            <p>
                                Оператор вправе производить как автоматизированную, так и не автоматизированную
                                обработку персональных данных, в том числе с использованием систем на основе
                                искусственного интеллекта, с передачей по внутренней сети Оператора.
                            </p>
                            <p>
                                Настоящее Согласие подписано простой электронной подписью (путём прохождения процесса
                                регистрации на сайте <a href={SITE_URL}>{SITE_URL}</a> и проставления соответствующей
                                отметки).
                            </p>
                            <p>Настоящее Согласие действует со дня его подписания и до:</p>
                            <p>
                                (1) дня достижения цели обработки персональных данных, если срок хранения персональных
                                данных не установлен федеральным законом, договором,
                            </p>
                            <p>
                                (2) момента отзыва настоящего Согласия путём личного обращения или направления
                                письменного обращения (в том числе в форме электронного документа, подписанного простой
                                электронной подписью или усиленной квалифицированной электронной подписью),
                                направляемого на электронный адрес Оператора{' '}
                                <a href="mailto:info@oilgasforum.ru">info@oilgasforum.ru</a> с пометкой «Отзыв согласия
                                на обработку персональных данных»).
                            </p>
                            <p>
                                Подтверждаю, что проинформирован(-а) о возможности отзыва настоящего Согласия у каждого
                                из лиц, указанных в настоящем Согласии.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-agreement"
                data={generateWebPageJsonLd({
                    name: h1,
                    description: meta.description,
                    pagePath: PAGE_PATH,
                    breadcrumbs,
                })}
            />
        </DefaultLayout>
    );
};

export default AgreementPage;

type AgreementPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
    }
>;

export const getStaticProps: GetStaticProps<AgreementPageProps> = async () => {
    const commonPageProps = await getCommonPageProps();

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'privacy-page',
            meta: {
                ...commonPageProps.meta,
                title: PAGE_TITLE,
                description: PAGE_DESCRIPTION,
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: PAGE_TITLE, href: PAGE_PATH },
            ],
            h1: PAGE_TITLE,
        } satisfies AgreementPageProps,
        revalidate: 60,
    };
};
